import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentUserVar, leftSidePanelVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import Link from "@components/link/link";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, getUrl } from "@helpers/helpers";

type Props = {
  menuIsCollapsed: boolean;
};

const SidebarManagersReports: React.FC<Props> = ({ menuIsCollapsed }) => {
  const currentUser = currentUserVar();
  const managers = assertEdgesNonNull(currentUser.managers);
  const directReports = assertEdgesNonNull(currentUser.directReports);
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

  const maxManagerThreshold = 7;
  const maxDirectReportsToShow = maxManagerThreshold - 2;
  const displayedDirectReports =
    directReports.length > maxManagerThreshold
      ? directReports.slice(0, maxDirectReportsToShow)
      : directReports;
  const reportListIsExpanded =
    uiPreferenceCache.leftNavigationDirectReportsListExpanded;

  // Hide entirely if no data
  if (managers.length === 0 && directReports.length === 0) {
    return null;
  }

  const handleToggleDirectReports = () => {
    const leftNavigationDirectReportsListExpanded =
      !uiPreferenceCache.leftNavigationDirectReportsListExpanded;
    saveUiPreference({
      leftNavigationDirectReportsListExpanded,
    });
  };

  const handleToggleFullList = () => {
    leftSidePanelVar("reports");
  };

  const renderUserRow = (user: any, isCollapsed: boolean) => {
    const profileUrl = getUrl({ userId: user.id });

    if (isCollapsed) {
      return (
        <Tooltip key={user.id} text={user.name} placement="right">
          <span>
            <Link
              to={profileUrl}
              className={classNames(
                "flex justify-center py-1 px-1 rounded-md hover:bg-gray-100",
                window.location.href.startsWith(
                  `${window.location.origin}${profileUrl}`,
                )
                  ? "bg-gray-100"
                  : "",
              )}
            >
              <Avatar user={user} size="5" showTooltip={false} />
            </Link>
          </span>
        </Tooltip>
      );
    }

    return (
      <Link
        key={user.id}
        to={profileUrl}
        className={classNames(
          "flex items-center gap-2 px-2 py-1 rounded hover:bg-nav-item-active",
          window.location.href.startsWith(
            `${window.location.origin}${profileUrl}`,
          )
            ? "bg-nav-item-active"
            : "",
        )}
      >
        <Avatar user={user} size="4" showTooltip={false} />
        <span className="text-gray-800 text-sm font-normal truncate">
          {user.name}
        </span>
      </Link>
    );
  };

  if (menuIsCollapsed) {
    return (
      <>
        <div className="mt-3">
          <div className="flex flex-col items-center py-2 gap-1">
            {displayedDirectReports.map((user) => renderUserRow(user, true))}
            {directReports.length > maxManagerThreshold && (
              <Button
                theme="blueText"
                size={ButtonSize.medium}
                onClick={handleToggleFullList}
              >
                <TopicflowIcon type="arrowRight" className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3 pt-5">
        {directReports.length > 0 && (
          <div className="">
            <button
              className="text-gray-500 text-xs px-2 py-1 flex items-center gap-0.5 rounded hover:bg-nav-item-active"
              onClick={handleToggleDirectReports}
            >
              <span>Reports ({directReports.length})</span>
              <TopicflowIcon
                type="chevronDown"
                className={classNames(
                  "h-3 w-3 text-gray-500 transition-transform duration-200 ease-in-out",
                  reportListIsExpanded ? "rotate-180" : "",
                )}
              />
            </button>
            <div
              className={classNames(
                "flex flex-col gap-0.5 overflow-hidden transition-all duration-200 ease-in-out",
                reportListIsExpanded
                  ? "max-h-96 opacity-100"
                  : "max-h-0 opacity-0",
              )}
            >
              {displayedDirectReports.map((user) => renderUserRow(user, false))}
            </div>
            {directReports.length > maxManagerThreshold && (
              <div className="mt-1">
                <Button
                  theme="blueText"
                  size={ButtonSize.medium}
                  onClick={handleToggleFullList}
                  className="text-[13px]"
                >
                  View all reports
                  <TopicflowIcon type="arrowRight" className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SidebarManagersReports;
